import { useEffect, useCallback } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import type {
  TracePreviewDto} from '@terra/shared-contracts-trace';
import {
  TRACE_CREATED_SSE_EVENT,
  TRACE_MESSAGE_ADDED_SSE_EVENT,
  TRACE_UPDATED_SSE_EVENT,
  type TraceSseMessage,
} from '@terra/shared-contracts-trace';

interface UseTracesSSEParams {
  applicationId: string;
  threadId?: string;
  enabled?: boolean;
  onTraceUpdate?: (trace: TracePreviewDto) => void;
  onMessagesAdded?: (messages: TraceSseMessage[]) => void;
  onTraceEvent?: () => void;
}

type TraceSSEEvent =
  | {
      type: 'TRACE_CREATED' | 'TRACE_UPDATED';
      data: {
        targetId: string;
        threadId: string;
        trace?: TracePreviewDto;
      };
    }
  | {
      type: 'TRACE_MESSAGE_ADDED';
      data: {
        targetId: string;
        threadId: string;
        messages?: TraceSseMessage[];
      };
    };

export function useTracesSSE({
  applicationId,
  threadId,
  enabled = true,
  onTraceUpdate,
  onMessagesAdded,
  onTraceEvent,
}: UseTracesSSEParams) {
  const { subscribe } = useSSE();

  const handleTraceEvent = useCallback(
    (event: TraceSSEEvent) => {
      if (event.data.targetId !== applicationId) {
        return;
      }

      // If threadId filter is provided, only process events for that thread
      if (threadId && event.data.threadId !== threadId) {
        return;
      }

      // Call onTraceEvent for any matching trace event (useful for triggering refetch)
      if (onTraceEvent) {
        onTraceEvent();
      }

      // Call onTraceUpdate only when trace data is available
      if (
        onTraceUpdate &&
        event.type !== 'TRACE_MESSAGE_ADDED' &&
        event.data.trace
      ) {
        onTraceUpdate(event.data.trace);
      }

      // Call onMessagesAdded when messages are included in the event
      if (
        onMessagesAdded &&
        event.type === 'TRACE_MESSAGE_ADDED' &&
        event.data.messages &&
        event.data.messages.length > 0
      ) {
        onMessagesAdded(event.data.messages);
      }
    },
    [applicationId, threadId, onTraceUpdate, onMessagesAdded, onTraceEvent],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribers = [
      subscribe(TRACE_CREATED_SSE_EVENT, (data) => {
        handleTraceEvent({
          type: 'TRACE_CREATED',
          data: {
            targetId: data.targetId,
            threadId: data.threadId,
            trace: data.trace,
          },
        });
      }),
      subscribe(TRACE_UPDATED_SSE_EVENT, (data) => {
        handleTraceEvent({
          type: 'TRACE_UPDATED',
          data: {
            targetId: data.targetId,
            threadId: data.threadId,
            trace: data.trace,
          },
        });
      }),
      subscribe(TRACE_MESSAGE_ADDED_SSE_EVENT, (data) => {
        handleTraceEvent({
          type: 'TRACE_MESSAGE_ADDED',
          data: {
            targetId: data.targetId,
            threadId: data.threadId,
            messages: data.messages,
          },
        });
      }),
    ];

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [subscribe, handleTraceEvent, enabled]);
}
